import React, { useState, useEffect } from 'react';
import HeaderSidebar from './HeaderSidebar';

const Header = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isSticky, setIsSticky] = useState(false);

  const handleSidebarToggle = () => {
    setSidebarOpen(true);
  };

  const handleSidebarClose = () => {
    setSidebarOpen(false);
  };

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuOpen(false);
  };

  // Handle dropdown icons for mobile menu
  useEffect(() => {
    const handleDropdownToggle = (e) => {
      const icon = e.target;
      if (icon.classList.contains('dropdown-icon')) {
        e.preventDefault();
        e.stopPropagation();
        icon.classList.toggle('active');
        const submenu = icon.parentElement.querySelector('.sub-menu');
        if (submenu) {
          if (submenu.style.display === 'block') {
            submenu.style.display = 'none';
          } else {
            submenu.style.display = 'block';
          }
        }
      }
    };

    // Use mousedown to capture before other events
    document.addEventListener('mousedown', handleDropdownToggle);
    document.addEventListener('touchstart', handleDropdownToggle);

    return () => {
      document.removeEventListener('mousedown', handleDropdownToggle);
      document.removeEventListener('touchstart', handleDropdownToggle);
    };
  }, []);

  useEffect(() => {
    const onScroll = () => {
      setIsSticky(window.scrollY > 0);
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Handle menu link clicks - allow navigation then close menu
  const handleMenuClick = (e) => {
    // Stop the click from reaching the overlay
    e.stopPropagation();
    
    // If clicking on a regular link (not dropdown toggle)
    if (e.target.tagName === 'A' && !e.target.classList.contains('drop-down')) {
      // Allow the link to work, then close menu after a short delay
      setTimeout(() => {
        setMobileMenuOpen(false);
      }, 150);
    }
  };

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mobileMenuOpen]);

  return (
    <>
      {/* Mobile Menu Overlay - only covers area outside menu */}
      {mobileMenuOpen && (
        <div
          className="mobile-menu-overlay"
          onClick={handleMobileMenuClose}
          style={{
            position: 'fixed',
            top: 0,
            left: 280,
            width: 'calc(100% - 280px)',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            zIndex: 99998,
          }}
        />
      )}

      <header className={`header-area2 one${isSticky ? ' sticky' : ''}`}>
        <div className="header-logo">
          <a href="/" className="header-logo-link d-inline-flex align-items-center">
            <img
              alt="Nalexus Technologies logo"
              src="/assets/img/nalexus-logo-tp.png"
              width={2550}
              height={810}
              style={{ height: 64, width: 'auto' }}
            />
          </a>
        </div>
        <div 
          className={`main-menu ${mobileMenuOpen ? 'show-menu' : ''}`}
          onClick={handleMenuClick}
        >
          <div className="mobile-logo-area d-lg-none d-flex justify-content-between align-items-center">
            <div className="mobile-logo-wrap">
              <a href="/">
                <img
                  alt="Nalexus Technologies logo"
                  src="/assets/img/nalexus-logo-tp.png"
                  width={2550}
                  height={810}
                  style={{ height: 48, width: 'auto' }}
                />
              </a>
            </div>
            <div className="menu-close-btn">
              <button
                type="button"
                onClick={handleMobileMenuClose}
                aria-label="Close mobile menu"
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  color: '#fff',
                  cursor: 'pointer',
                  padding: '8px 10px',
                }}
              >
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
          </div>
          <ul className="menu-list">
            <li><a href="/">Home</a></li>
            <li><a href="/about">About</a></li>
            <li className="menu-item-has-children">
              <a href="/services" className="drop-down">Services</a>
              <i className="bi bi-plus dropdown-icon"></i>
              <ul className="sub-menu">
                <li>
                  <a href="/services/custom-software-enterprise-applications">
                    Custom Software & Enterprise Apps
                  </a>
                </li>
                <li>
                  <a href="/services/cms-digital-experience-platforms">
                    CMS & Digital Experience Platforms
                  </a>
                </li>
                <li>
                  <a href="/services/workflow-automation-system-integrations">
                    Workflow Automation & Integrations
                  </a>
                </li>
                <li><a href="/services/generative-ai-solutions">Generative AI Solutions</a></li>
                <li><a href="/services/ai-systems-integrations">AI Systems & Integrations</a></li>
                <li><a href="/services/saas-product-engineering">SaaS Product Engineering</a></li>
              </ul>
            </li>
            <li><a href="/case-studies">Case Studies</a></li>
            <li><a href="/articles">Articles</a></li>
            <li><a href="/course-registration">Courses</a></li>
            <li><a href="/contact">Contact</a></li>
          </ul>
          <div className="d-lg-none d-block">
            <form className="mobile-menu-form">
              <div className="hotline pt-30">
                <div className="hotline-icon">
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M22 16.92V19.92C22.0011 20.1985 21.9441 20.4742 21.8325 20.7293C21.7209 20.9845 21.5573 21.2136 21.3521 21.4019C21.1468 21.5901 20.9046 21.7335 20.6407 21.8227C20.3769 21.9119 20.0974 21.9451 19.82 21.92C16.7428 21.5856 13.787 20.5341 11.19 18.85C8.77382 17.3147 6.72533 15.2662 5.18999 12.85C3.49997 10.2412 2.44824 7.27099 2.11999 4.18C2.095 3.90347 2.12787 3.62476 2.21649 3.36162C2.30512 3.09849 2.44756 2.85669 2.63476 2.65162C2.82196 2.44655 3.0498 2.28271 3.30379 2.17052C3.55777 2.05833 3.83233 2.00026 4.10999 2H7.10999C7.5953 1.99522 8.06579 2.16708 8.43376 2.48353C8.80173 2.79999 9.04207 3.23945 9.10999 3.72C9.23662 4.68007 9.47144 5.62273 9.80999 6.53C9.94454 6.88792 9.97366 7.27691 9.8939 7.65088C9.81415 8.02485 9.62886 8.36811 9.35999 8.64L8.08999 9.91C9.51355 12.4135 11.5864 14.4864 14.09 15.91L15.36 14.64C15.6319 14.3711 15.9751 14.1858 16.3491 14.1061C16.7231 14.0263 17.1121 14.0555 17.47 14.19C18.3773 14.5286 19.3199 14.7634 20.28 14.89C20.7658 14.9585 21.2094 15.2032 21.5265 15.5775C21.8437 15.9518 22.0122 16.4296 22 16.92Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div className="hotline-info">
                  <span>Call Our Team</span>
                  <h6><a href="tel:+923270738451">+92 327 0738451</a></h6>
                  <h6><a href="tel:+923277477744">+92 327 7477744</a></h6>
                </div>
              </div>
                <div className="email pt-20 d-flex align-items-center">
                  <div className="email-icon">
                    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M22 6L12 13L2 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                <div className="email-info">
                  <span>Email Us</span>
                  <h6><a href="mailto:contact@nalexustechnologies.com">contact@nalexustechnologies.com</a></h6>
                </div>
              </div>
            </form>
            <div className="header-btn5">
              <a className="primary-btn3" href="/course-registration">Enroll Now</a>
            </div>
          </div>
        </div>
        <div className="nav-right d-flex justify-content-end align-items-center">
          <button
            type="button"
            className={`mobile-menu-btn ${mobileMenuOpen ? 'active' : ''}`}
            onClick={handleMobileMenuToggle}
            aria-label="Toggle mobile menu"
            style={{
              marginRight: '15px',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = '0.8';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = '1';
            }}
          >
            <span className="menu-bar-line"></span>
          </button>
          <button
            type="button"
            className="sidebar-btn2"
            onClick={handleSidebarToggle}
            aria-label="Open sidebar menu"
            style={{
              background: 'linear-gradient(135deg, #c79528 0%, #b68a20 100%)',
              border: '1px solid rgba(223,169,37,0.32)',
              boxShadow: 'none',
              borderRadius: 12,
              padding: 10,
            }}
          >
            <img src="/assets/img/content/sidebar-btn.svg" alt="" aria-hidden="true" />
          </button>
        </div>
      </header>
      <HeaderSidebar isOpen={sidebarOpen} onClose={handleSidebarClose} />
    </>
  );
};

export default Header;




